/**
 * A large table draws the TOP fifty of a ranking the planner chooses, after the filters the
 * planner sets — `docs/adr/9034`, owner ruling of 2026-10-01.
 *
 * ADR 9008 capped a table at its first fifty rows in the order the chapter handed it, and a few
 * screens escaped the cap by windowing every row instead (the stock grid, the transfer grids, the
 * decision record): a run of five to seven thousand lines was "reachable" by scrolling and read by
 * nobody. The ruling closes both: fifty rows at most, and the planner steers WHICH fifty — a
 * ranking (units, net benefit, cover…) and a few filters (store, purpose, decision…). The rest is
 * not lost: the count says how many rows match and how many there are, and the CSV carries every
 * row that matches.
 *
 *   rows ──► filter ──► rank ──► top 50 ──► draw
 *                          └──────────────► CSV (the FILTERED set, in rank order)
 *                          └──────────────► a bulk decision (the FILTERED set, never the fifty)
 *
 * **Client-side, over the whole read.** No endpoint this front end calls takes a sort or a filter
 * parameter, so the ranking and the filters run here over the rows the page read — every page of
 * them (`useApiCollection` reads to the end, ADR 9009) — not over a prefix. (A page whose platform
 * ranks and caps in the store — the Allocation index, bata-platform ADR 6179 — hands the bar the top
 * rows it was sent and how many match, `RankedRows.matching`, and builds its file on the click.)
 *
 * **The URL is the state** (CLAUDE.md §3.4): `?rank=<option>` (or `<option>:asc|desc` when the
 * planner turned the default direction round) and one `?f.<filter>=<value>` per filter set, so a
 * ranked, filtered view is a link. A `multi` filter puts its collected values on that one key,
 * comma-separated — `?f.store=5154,5160,5201` — so collecting four doors is still one link, and a
 * link naming a single value reads as it always did. A page drawing two ranked tables gives one a `namespace`
 * (`?queue.rank=…`). The parse and the write are pure over a search string; the hook reads
 * `window.location` and writes it with `replaceState` — re-ranking a list is not a page the back
 * button should step through, and nothing else on the page re-reads these keys.
 *
 * **No absence is a small number.** A row whose ranked value is absent sorts last in both
 * directions, as every table here sorts it (ADR 9009).
 */

import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isAbsent, type ColumnGuideSource, type ExportRequest, type ProvenanceFilter } from '../export';
import { AboutPopover, Combobox, FOCUS_RING } from '../primitives';
import { carriedSearch } from './carried-search';
import { RowCapNotice, TABLE_ROW_CAP, type DownloadLabelling } from './row-cap';

// ── the contract ─────────────────────────────────────────────────────────────

export type RankDirection = 'asc' | 'desc';

/** One way the planner may rank the rows. */
export interface RankOption<T> {
  /** The `?rank=` value. */
  readonly id: string;
  /** How the option reads in the control and in the count sentence — «net benefit». */
  readonly labelKey: string;
  /** What the rows are ranked on. `null`/`undefined` is an absence: last in both directions. */
  readonly value: (row: T) => string | number | null | undefined;
  /** The direction the option ranks in unless the planner turns it: a measure largest first. */
  readonly direction: RankDirection;
  /**
   * What breaks a tie on `value`, always in its OWN direction — turning the ranking round does not
   * turn the tie-break (brief part 3 §C3: by status, then net benefit largest first, ADR 9034's
   * amendment of 2026-10-01). An absence is last. Without it, ties keep the order given.
   */
  readonly tieBreak?: { readonly value: (row: T) => number | null | undefined; readonly direction: RankDirection };
}

/** One filter over the rows. */
export interface RankFilter<T> {
  /** The URL key is `f.<id>`. */
  readonly id: string;
  readonly labelKey: string;
  /**
   * `select` picks one value among those present (a purpose, a decision, a size scale); `search`
   * matches text anywhere in the value, case-insensitively; `multi` COLLECTS values — one or
   * several of those present, each matched exactly — for a dimension with too many values to
   * put in a `select` and too many for one substring to name (a store, an article: an allocator
   * comparing four doors wants the four, not a text fragment that happens to catch them).
   *
   * A `multi` carries its values comma-separated on ONE `f.<id>` key, so a collected view is a
   * link like any other and a single value reads the same as it always did. The comma is
   * therefore not available inside a value: `multiValues` would split it. No dimension collected
   * here has one (a store key, an article key, a code), and a value containing a comma simply
   * never matches rather than matching the wrong row.
   */
  readonly kind: 'select' | 'search' | 'multi';
  readonly value: (row: T) => string | null | undefined;
  /** A `select` or `multi` value as the planner reads it — «Bogotá Centro (5154)» for `5154`. */
  readonly label?: (value: string) => string;
  /**
   * The values a `select` or a `multi` offers when they come from a vocabulary rather than from the rows read —
   * a filter the platform applies (a department family: no row carries it, `value` answers from
   * the platform's narrowed read). Absent: the values present in the rows.
   */
  readonly options?: readonly string[];
}

/**
 * A value as the planner reads it, for the filters that offer values (`select`, `multi`): the
 * filter's own `label`, else the value itself — the raw code. The fallback is deliberately in
 * ONE place: a code is not a name, and the day the platform publishes vocabulary labels this is
 * the single function that learns them, for every filter on every table.
 */
export function valueLabel<T>(filter: Pick<RankFilter<T>, 'label'>): (value: string) => string {
  return filter.label ?? ((value) => value);
}

export interface RankedCapConfig<T> {
  readonly rankOptions: readonly RankOption<T>[];
  readonly filters?: readonly RankFilter<T>[];
  /** The option ranked by when the URL names none. The first option otherwise. */
  readonly defaultRank?: string;
  /** Rows drawn. Default fifty — the same bound as every table (ADR 9008). */
  readonly cap?: number;
  /** Prefixes the URL keys, for a page with two ranked tables: `queue` → `?queue.rank=`. */
  readonly namespace?: string;
}

/** The ranking and the filters, as the URL states them. */
export interface RankedScope {
  /** The option id and the direction; `null` for the config's default. */
  readonly rank: { readonly id: string; readonly direction: RankDirection | null } | null;
  /** Filter id → value, only the filters that are set. */
  readonly filters: Readonly<Record<string, string>>;
}

export interface RankedRows<T> {
  /** The rows drawn: the top `cap` of `filtered`. */
  readonly shown: readonly T[];
  /** Every row that passes the filters, in rank order — what the CSV and a bulk decision take. */
  readonly filtered: readonly T[];
  /** Every row read. */
  readonly total: number;
  /**
   * How many rows pass the filters, when `filtered` holds fewer than that — a table the platform ranks
   * and caps in the store (the Allocation index, bata-platform ADR 6179), whose `filtered` is the rows it
   * sent and whose file is built when the planner asks for it. Absent: `filtered.length`, as always.
   */
  readonly matching?: number;
}

export interface RankedCap<T> extends RankedRows<T> {
  readonly config: RankedCapConfig<T>;
  readonly rank: RankOption<T> | null;
  readonly direction: RankDirection;
  readonly filterValues: Readonly<Record<string, string>>;
  /** The distinct values each `select` filter offers, over every row read. */
  readonly choices: Readonly<Record<string, readonly string[]>>;
  readonly setRank: (id: string, direction?: RankDirection) => void;
  readonly setFilter: (id: string, value: string | null) => void;
  readonly clearFilters: () => void;
  /** At least one filter is set. */
  readonly filtering: boolean;
}

// ── the pure part ────────────────────────────────────────────────────────────

/**
 * The values a `multi` filter's URL value names — comma-separated, trimmed, in the order they
 * were collected, each once. An empty string is no value, so `?f.store=` filters nothing. Pure.
 */
export function multiValues(raw: string | undefined): readonly string[] {
  if (raw === undefined) return [];
  const seen = new Set<string>();
  for (const part of raw.split(',')) {
    const value = part.trim();
    if (value !== '') seen.add(value);
  }
  return [...seen];
}

/** `values` as a `multi` filter's URL value. Empty gives `''`, which clears the filter. Pure. */
export function joinMultiValues(values: readonly string[]): string {
  return multiValues(values.join(',')).join(',');
}

const keyOf = (namespace: string | undefined, key: string): string => (namespace === undefined ? key : `${namespace}.${key}`);
const rankKey = (namespace?: string): string => keyOf(namespace, 'rank');
const filterKey = (namespace: string | undefined, id: string): string => keyOf(namespace, `f.${id}`);

/** The ranking and filters `search` names, restricted to those `config` declares. Pure. */
export function readRankedScope<T>(search: string, config: RankedCapConfig<T>): RankedScope {
  const params = new URLSearchParams(search);
  const raw = params.get(rankKey(config.namespace));
  let rank: RankedScope['rank'] = null;
  if (raw !== null && raw.trim() !== '') {
    const at = raw.lastIndexOf(':');
    const tail = at === -1 ? '' : raw.slice(at + 1);
    const turned = tail === 'asc' || tail === 'desc';
    const id = turned ? raw.slice(0, at) : raw;
    if (config.rankOptions.some((option) => option.id === id)) {
      rank = { id, direction: turned ? tail : null };
    }
  }
  const filters: Record<string, string> = {};
  for (const filter of config.filters ?? []) {
    const value = params.get(filterKey(config.namespace, filter.id));
    if (value !== null && value.trim() !== '') filters[filter.id] = value;
  }
  return { rank, filters };
}

/** `search` with the ranking and filters of `scope` written, every other key kept. Pure. */
export function writeRankedScope<T>(search: string, scope: RankedScope, config: RankedCapConfig<T>): string {
  const params = new URLSearchParams(search);
  const option = scope.rank === null ? undefined : config.rankOptions.find((candidate) => candidate.id === scope.rank?.id);
  if (option === undefined || scope.rank === null) params.delete(rankKey(config.namespace));
  else {
    const turned = scope.rank.direction !== null && scope.rank.direction !== option.direction;
    params.set(rankKey(config.namespace), turned ? `${option.id}:${scope.rank.direction}` : option.id);
  }
  for (const filter of config.filters ?? []) {
    const value = scope.filters[filter.id];
    if (value === undefined || value.trim() === '') params.delete(filterKey(config.namespace, filter.id));
    else params.set(filterKey(config.namespace, filter.id), value);
  }
  const next = params.toString();
  return next === '' ? '' : `?${next}`;
}

/**
 * `path` with a ranked view's ranking and filters (`?<ns.>rank=`, `?<ns.>f.<id>=`) replaced by `filters` and
 * the page's own parameters kept; `fragment` is the id of the element to scroll to. A KPI card counts the whole
 * list (or one filter of it), so the ranked list it opens must not keep a ranking or filter set before
 * (ADR 9069). `carry`, when given, names the ONLY parameters of the page that travel (as in `tableFilterHref`): a link to
 * another page keeps the scope the card counted under and nothing of this page's cut. Pure.
 */
export function rankedViewHref(
  path: string,
  search: string,
  filters: Readonly<Record<string, string>>,
  fragment?: string,
  namespace?: string,
  carry?: readonly string[],
): string {
  const params = new URLSearchParams(carry === undefined ? search : carriedSearch(search, carry));
  for (const key of [...params.keys()]) {
    if (key === rankKey(namespace) || key.startsWith(filterKey(namespace, ''))) params.delete(key);
  }
  for (const [id, value] of Object.entries(filters)) params.set(filterKey(namespace, id), value);
  const query = params.toString();
  return `${path}${query === '' ? '' : `?${query}`}${fragment === undefined ? '' : `#${fragment}`}`;
}

/** The option the scope ranks by, and its direction. Pure. */
export function resolveRank<T>(scope: RankedScope, config: RankedCapConfig<T>): {
  readonly option: RankOption<T> | null;
  readonly direction: RankDirection;
} {
  const id = scope.rank?.id ?? config.defaultRank;
  const option = config.rankOptions.find((candidate) => candidate.id === id) ?? config.rankOptions[0] ?? null;
  const direction = (scope.rank !== null && scope.rank.id === option?.id ? scope.rank.direction : null)
    ?? option?.direction ?? 'desc';
  return { option, direction };
}

const present = (value: string | number | null | undefined): value is string | number =>
  value !== null && value !== undefined && !(typeof value === 'number' && Number.isNaN(value));

/** Whether `row` passes every filter set in `scope`. Pure. */
export function passesFilters<T>(row: T, scope: RankedScope, config: RankedCapConfig<T>): boolean {
  for (const filter of config.filters ?? []) {
    const wanted = scope.filters[filter.id];
    if (wanted === undefined) continue;
    const value = filter.value(row);
    if (value === null || value === undefined) return false;
    if (filter.kind === 'select') {
      if (value !== wanted) return false;
    } else if (filter.kind === 'multi') {
      // Collected values are matched EXACTLY and joined by OR: the planner picked them from the
      // values present, so a substring match would widen a deliberate choice.
      const wantedValues = multiValues(wanted);
      if (wantedValues.length > 0 && !wantedValues.includes(value)) return false;
    } else if (!value.toLocaleLowerCase().includes(wanted.trim().toLocaleLowerCase())) return false;
  }
  return true;
}

/**
 * Filter, rank, cap — the whole mechanism, pure. Ties keep the order the rows were given in, so
 * a chapter's own order breaks them.
 */
export function rankRows<T>(rows: readonly T[], config: RankedCapConfig<T>, scope: RankedScope): RankedRows<T> {
  const { option, direction } = resolveRank(scope, config);
  const matching = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => passesFilters(row, scope, config));
  if (option !== null) {
    const sign = direction === 'asc' ? 1 : -1;
    const tie = option.tieBreak;
    const keyed = matching.map((entry) => ({
      ...entry, key: option.value(entry.row), tie: tie === undefined ? null : tie.value(entry.row),
    }));
    keyed.sort((a, b) => {
      const left = present(a.key);
      const right = present(b.key);
      if (left !== right) return left ? -1 : 1; // an absence is last in BOTH directions
      if (left && right) {
        const order = typeof a.key === 'number' && typeof b.key === 'number'
          ? a.key - b.key
          : String(a.key).localeCompare(String(b.key), undefined, { numeric: true });
        if (order !== 0) return sign * order;
      }
      if (tie !== undefined) {
        const tieLeft = present(a.tie);
        const tieRight = present(b.tie);
        if (tieLeft !== tieRight) return tieLeft ? -1 : 1;
        if (tieLeft && tieRight && a.tie !== b.tie) {
          return (tie.direction === 'asc' ? 1 : -1) * ((a.tie as number) - (b.tie as number));
        }
      }
      return a.index - b.index;
    });
    matching.splice(0, matching.length, ...keyed);
  }
  const filtered = matching.map((entry) => entry.row);
  const cap = config.cap ?? TABLE_ROW_CAP;
  return { shown: filtered.length > cap ? filtered.slice(0, cap) : filtered, filtered, total: rows.length };
}

/**
 * The distinct values a `select` filter offers — its vocabulary where it has one, else every value
 * over every row — sorted. The value `chosen` is always offered: a link naming a value the rows
 * read do not carry (the next page's store, a decided group) still shows what it filters by,
 * instead of a select that silently reads «Any» over an empty table. Pure.
 */
export function filterChoices<T>(rows: readonly T[], filter: RankFilter<T>, chosen?: string | null): readonly string[] {
  const values = new Set<string>(filter.options ?? []);
  if (filter.options === undefined) {
    for (const row of rows) {
      const value = filter.value(row);
      if (value !== null && value !== undefined && value !== '') values.add(value);
    }
  }
  if (chosen !== undefined && chosen !== null && chosen !== '') {
    // A `multi` carries several values on one key; each of them is offered, for the same reason.
    if (filter.kind === 'multi') for (const value of multiValues(chosen)) values.add(value);
    else values.add(chosen);
  }
  const label = valueLabel(filter);
  return [...values].sort((a, b) => label(a).localeCompare(label(b), undefined, { numeric: true }));
}

// ── the export's perimeter ───────────────────────────────────────────────────

/**
 * The filters set, as the export's provenance states them — one `(id, equals|contains, value)`
 * triple per filter, the value as the URL carries it (a code, not its label), the same form the
 * Parameters filters are written in. Pure.
 */
export function rankedProvenanceFilters<T>(
  config: RankedCapConfig<T>, values: Readonly<Record<string, string>>,
): readonly ProvenanceFilter[] {
  return (config.filters ?? []).flatMap((filter) => {
    const value = values[filter.id]?.trim();
    if (value === undefined || value === '') return [];
    // A collected filter states ONE triple per value it collected, each an `equals`: «store
    // equals 5154» and «store equals 5160». A single `contains '5154,5160'` would describe a
    // perimeter no row is in, and the provenance is what a reader reconciles the file against.
    if (filter.kind === 'multi') {
      return multiValues(value).map((one) => ({ field: filter.id, operator: 'equals', value: one }));
    }
    return [{ field: filter.id, operator: filter.kind === 'select' ? 'equals' : 'contains', value }];
  });
}

/**
 * `request` with the ranked table's filters and ranking added to its perimeter, and to its
 * provenance where the provenance states a filter list (a declared absence stays declared: the
 * caller knows why it cannot claim the list, and part of a list would be a wrong claim). A file
 * downloaded from a filtered view says which filter made it — G2.4, the provenance's field 1. Pure.
 */
export function withRankedScope<T>(
  request: ExportRequest,
  scope: { readonly config: RankedCapConfig<T>; readonly filterValues: Readonly<Record<string, string>>;
    readonly rank: RankOption<T> | null; readonly direction: RankDirection },
): ExportRequest {
  const filters = rankedProvenanceFilters(scope.config, scope.filterValues);
  const sort = scope.rank === null ? [] : [{ key: scope.rank.id, direction: scope.direction }];
  if (filters.length === 0 && sort.length === 0) return request;
  const known = (list: readonly ProvenanceFilter[], filter: ProvenanceFilter): boolean =>
    list.some((entry) => entry.field === filter.field && entry.operator === filter.operator && entry.value === filter.value);
  const provenanceFilters = request.provenance.filters;
  return {
    ...request,
    provenance: {
      ...request.provenance,
      filters: isAbsent(provenanceFilters)
        ? provenanceFilters
        : [...provenanceFilters, ...filters.filter((filter) => !known(provenanceFilters, filter))],
    },
    perimeter: {
      ...request.perimeter,
      filters: [...request.perimeter.filters, ...filters.filter((filter) => !known(request.perimeter.filters, filter))],
      sort: request.perimeter.sort.length > 0 ? request.perimeter.sort : sort,
    },
  };
}

// ── the hook ─────────────────────────────────────────────────────────────────

/** Whether two scopes say the same thing. Pure. */
export function sameScope(a: RankedScope, b: RankedScope): boolean {
  if ((a.rank?.id ?? null) !== (b.rank?.id ?? null) || (a.rank?.direction ?? null) !== (b.rank?.direction ?? null)) return false;
  const keys = new Set([...Object.keys(a.filters), ...Object.keys(b.filters)]);
  for (const key of keys) if (a.filters[key] !== b.filters[key]) return false;
  return true;
}

function currentSearch(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

/**
 * The ranked, filtered, capped view of `rows`, with its state in the URL. `config` should be
 * memoised by the caller: its options are read on every render, not captured.
 */
export function useRankedCap<T>(rows: readonly T[], config: RankedCapConfig<T>): RankedCap<T> {
  const [scope, setScope] = useState<RankedScope>(() => readRankedScope(currentSearch(), config));

  // Back and forward, and an in-app link that lands with other keys: re-read the address. Also
  // when the options change — an option offered only once the rows land (a queue's «value») is
  // honoured from the URL rather than dropped by the first, empty, parse. The URL is the state.
  useEffect(() => {
    const reread = (): void => {
      const next = readRankedScope(currentSearch(), config);
      setScope((current) => (sameScope(current, next) ? current : next));
    };
    reread();
    const onPopState = reread;
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [config]);

  const commit = useCallback((next: RankedScope): void => {
    setScope(next);
    if (typeof window === 'undefined') return;
    const search = writeRankedScope(window.location.search, next, config);
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}${window.location.hash}`);
  }, [config]);

  const ranked = useMemo(() => rankRows(rows, config, scope), [rows, config, scope]);
  const { option, direction } = resolveRank(scope, config);
  const choices = useMemo(() => Object.fromEntries(
    (config.filters ?? []).filter((filter) => filter.kind === 'select' || filter.kind === 'multi')
      .map((filter) => [filter.id, filterChoices(rows, filter, scope.filters[filter.id])]),
  ), [rows, config, scope.filters]);

  return {
    ...ranked,
    config,
    rank: option,
    direction,
    filterValues: scope.filters,
    choices,
    filtering: Object.keys(scope.filters).length > 0,
    setRank: (id, turned) => commit({ ...scope, rank: { id, direction: turned ?? null } }),
    setFilter: (id, value) => {
      const filters = { ...scope.filters };
      if (value === null || value.trim() === '') delete filters[id];
      else filters[id] = value;
      commit({ ...scope, filters });
    },
    clearFilters: () => commit({ ...scope, filters: {} }),
  };
}

// ── the control ──────────────────────────────────────────────────────────────

const FIELD = `border-hairline bg-surface-raised text-ink-primary h-[32px] rounded-control border px-2 text-[13px] ${FOCUS_RING}`;
const LABEL = 'text-ink-secondary text-[11px] font-bold uppercase tracking-[0.06em]';

/**
 * How many matches a `multi` filter's list draws at once. A run of seven thousand lines carries
 * some hundreds of stores and some thousands of articles. Every one of them is reachable — the
 * list is searched, not paged — but drawing thousands of rows in a popover is slow and no
 * faster to read, so the list draws the first five hundred that MATCH what the planner typed and
 * says, inside the list, that typing narrows it further. The search covers every value; the
 * cap is on what is drawn. The narrowing is over the values, not over the table: the table is
 * filtered by what has been collected, which is the whole point of the control.
 */
const MULTI_LIST_CAP = 500;

interface MultiFilterFieldProps {
  readonly fieldId: string;
  readonly labelKey: string;
  /** Every value the rows carry for this filter, sorted, plus whatever is already collected. */
  readonly choices: readonly string[];
  /** What is collected now, in the order it was collected. */
  readonly values: readonly string[];
  readonly label: (value: string) => string;
  readonly onChange: (values: readonly string[]) => void;
  readonly filterId: string;
}

/**
 * A list collector: the values collected as removable chips, and ONE searchable box that adds
 * one. The box is the design system's {@link Combobox} (ARIA 1.2: arrow keys, Home/End, Enter to
 * add, Escape to close) in its collecting mode — typing narrows the list inside the dropdown, a
 * pick adds a chip and leaves the box ready for the next value. Each addition and each removal
 * re-filters the table at once, through the same `setFilter` a `select` uses — the URL is still
 * the state.
 *
 * Typing is explicitly NOT a filter — a planner who types and collects nothing has changed
 * nothing, which is the behaviour a `search` filter could not give (it filtered on every
 * keystroke).
 */
function MultiFilterField({
  fieldId, labelKey, choices, values, label, onChange, filterId,
}: MultiFilterFieldProps): React.JSX.Element {
  const { t } = useTranslation();
  const collected = new Set(values);
  const options = choices
    .filter((choice) => !collected.has(choice))
    .map((choice) => ({ value: choice, label: label(choice) }));

  return (
    <div className="flex flex-col gap-1">
      <Combobox
        id={fieldId}
        labelKey={labelKey}
        labelClassName={LABEL}
        className="w-[14rem]"
        placeholderKey="table.ranked.multiSearch"
        options={options}
        emptyKey={options.length === 0 ? 'table.ranked.multiNoneLeft' : 'field.noMatches'}
        collect
        matchValue
        maxOptions={MULTI_LIST_CAP}
        moreKey="table.ranked.multiMore"
        onValueChange={(value) => onChange([...values, value])}
        inputData={{ 'data-ranked-filter': filterId }}
      />
      {values.length > 0 && (
        <ul data-ranked-chips={filterId} className="flex max-w-[22rem] flex-wrap items-center gap-1">
          {values.map((value) => (
            <li key={value}>
              <button
                type="button"
                onClick={() => onChange(values.filter((kept) => kept !== value))}
                aria-label={t('table.ranked.multiRemove', { value: label(value) })}
                title={t('table.ranked.multiRemove', { value: label(value) })}
                className={`bg-surface-sunken border-hairline text-ink-secondary inline-flex items-center gap-1 rounded-chip border px-2 py-1 text-[11px] ${FOCUS_RING}`}
              >
                <span>{label(value)}</span>
                <span aria-hidden>&times;</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export interface RankFilterBarProps<T> {
  readonly ranked: RankedCap<T>;
  /** The CSV of THIS table's filtered rows, in rank order. Absent: no download. */
  readonly exportRequest?: (filtered: readonly T[]) => ExportRequest | Promise<ExportRequest>;
  /**
   * The table carries a decision over a set — «decide by type», «approve the group»: the bar
   * then says, once, that the decision takes every matching row and not only the fifty drawn.
   */
  readonly bulk?: boolean;
  /**
   * `false` draws the controls alone: a page whose ranking is shared by several tables (the
   * transfer page's one grid per action type) states each table's count over it with
   * {@link RankedCountLine}.
   */
  readonly showCount?: boolean;
  /** The export's columns, when it explains them: a «Column guide» download beside the CSV and Excel (`docs/adr/9045`). */
  readonly columnGuide?: ColumnGuideSource;
  /** What one row of the table is, said in the count and its (i) (bata-ui#302 A). Absent: «rows». */
  readonly grain?: RankedGrain<T>;
  /** What the download buttons say the files hold, when it is not the row count (a file that leaves lines out). */
  readonly download?: DownloadLabelling<T>;
  /**
   * The platform ranks each option in one direction only (a cursor-paged ranking, ADR 9018): the
   * direction toggle is not drawn, since turning it could not be honoured.
   */
  readonly directionFixed?: boolean;
}

/**
 * What one row of a ranked table IS, said in its count (bata-ui#302 A): «… of 8,558 rows (store ×
 * article × size)», or «… of 5,421 store × article pairs (8,558 size lines); the file holds the
 * 8,558 lines». A page whose rows group the lines its file carries says both numbers, so the count
 * on screen and the rows in the file never seem to disagree. The (i) beside the count says the
 * same at length (DEC-110/111).
 */
export interface RankedGrain<T> {
  /** «Top {shown} of {filtered} …, ranked by {rank}.» — the unfiltered count. */
  readonly countKey: string;
  /** «Top {shown} of {filtered} … (of {total}), ranked by {rank}.» — the filtered count. */
  readonly countFilteredKey: string;
  /** Further figures the two sentences name, over the rows that match — the file's lines, say. */
  readonly values?: (filtered: readonly T[]) => Readonly<Record<string, number>>;
  /** The (i) beside the count: what one row is, and what the file holds. */
  readonly descriptionKey: string;
  readonly descriptionValues?: Readonly<Record<string, string | number>>;
}

/**
 * The toolbar above a ranked table: rank by, the filters, the count — "top {shown} of {filtered}
 * matching (of {total}), by {rank}" — the CSV of every matching row, and, over a table that
 * decides, what a bulk decision takes.
 */
export function RankFilterBar<T>({
  ranked, exportRequest, bulk = false, showCount = true, columnGuide, grain, download, directionFixed = false,
}: RankFilterBarProps<T>): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  const { config, rank, direction } = ranked;

  return (
    <div data-ranked-bar="" data-tour="ranked-bar" className="flex flex-col gap-2">
      {/* `data-table-control`: rank-by and the filters are controls; the count line under them prints (ADR 9053). */}
      <div data-table-control="rank" className="flex flex-wrap items-end gap-3">
        {config.rankOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label htmlFor={`${id}-rank`} className={LABEL}>{t('table.ranked.rankBy')}</label>
            <span className="flex items-center gap-1">
              <select
                id={`${id}-rank`}
                data-ranked-rank=""
                value={rank?.id ?? ''}
                onChange={(event) => ranked.setRank(event.target.value)}
                className={FIELD}
              >
                {config.rankOptions.map((option) => (
                  <option key={option.id} value={option.id}>{t(option.labelKey)}</option>
                ))}
              </select>
              {!directionFixed && (
                <button
                  type="button"
                  data-ranked-direction={direction}
                  onClick={() => { if (rank !== null) ranked.setRank(rank.id, direction === 'desc' ? 'asc' : 'desc'); }}
                  aria-label={t(direction === 'desc' ? 'table.ranked.descending' : 'table.ranked.ascending')}
                  title={t(direction === 'desc' ? 'table.ranked.descending' : 'table.ranked.ascending')}
                  className={`${FIELD} font-numeric`}
                >
                  <span aria-hidden>{direction === 'desc' ? '↓' : '↑'}</span>
                </button>
              )}
            </span>
          </div>
        )}
        {(config.filters ?? []).map((filter) => {
          const value = ranked.filterValues[filter.id] ?? '';
          const fieldId = `${id}-f-${filter.id}`;
          if (filter.kind === 'multi') {
            return (
              <MultiFilterField
                key={filter.id}
                fieldId={fieldId}
                filterId={filter.id}
                labelKey={filter.labelKey}
                choices={ranked.choices[filter.id] ?? []}
                values={multiValues(value)}
                label={valueLabel(filter)}
                onChange={(next) => ranked.setFilter(filter.id, joinMultiValues(next))}
              />
            );
          }
          return (
            <div key={filter.id} className="flex flex-col gap-1">
              <label htmlFor={fieldId} className={LABEL}>{t(filter.labelKey)}</label>
              {filter.kind === 'select' ? (
                <select
                  id={fieldId}
                  data-ranked-filter={filter.id}
                  value={value}
                  onChange={(event) => ranked.setFilter(filter.id, event.target.value === '' ? null : event.target.value)}
                  className={FIELD}
                >
                  <option value="">{t('table.ranked.any')}</option>
                  {(ranked.choices[filter.id] ?? []).map((choice) => (
                    <option key={choice} value={choice}>{valueLabel(filter)(choice)}</option>
                  ))}
                </select>
              ) : (
                <input
                  id={fieldId}
                  type="search"
                  data-ranked-filter={filter.id}
                  value={value}
                  onChange={(event) => ranked.setFilter(filter.id, event.target.value)}
                  className={FIELD}
                />
              )}
            </div>
          );
        })}
        {ranked.filtering && (
          <button type="button" onClick={ranked.clearFilters} className={`text-ink-primary h-[32px] px-2 text-[13px] underline ${FOCUS_RING}`}>
            {t('table.filter.clearAll')}
          </button>
        )}
      </div>
      {showCount && (
        <RankedCountLine
          shown={ranked.shown.length}
          filtered={ranked.filtered}
          total={ranked.total}
          rankLabelKey={rank?.labelKey ?? null}
          bulk={bulk}
          {...(ranked.matching === undefined ? {} : { matching: ranked.matching })}
          {...(exportRequest === undefined ? {} : {
            exportRequest: async (filtered: readonly T[]) => withRankedScope(await exportRequest(filtered), ranked),
          })}
          {...(columnGuide === undefined ? {} : { columnGuide })}
          {...(grain === undefined ? {} : { grain })}
          {...(download === undefined ? {} : { download })}
        />
      )}
    </div>
  );
}

export interface RankedCountLineProps<T> {
  readonly shown: number;
  /** Every matching row, in rank order — the CSV's rows. */
  readonly filtered: readonly T[];
  readonly total: number;
  readonly rankLabelKey: string | null;
  readonly bulk?: boolean;
  /** How many rows match when `filtered` holds fewer (`RankedRows.matching`); absent: `filtered.length`. */
  readonly matching?: number;
  readonly exportRequest?: (filtered: readonly T[]) => ExportRequest | Promise<ExportRequest>;
  readonly columnGuide?: ColumnGuideSource;
  /** What one row is — the count's words and its (i). Absent: «rows». */
  readonly grain?: RankedGrain<T>;
  readonly download?: DownloadLabelling<T>;
}

/**
 * One ranked table's count — "top {shown} of {filtered} matching (of {total}), ranked by {rank}"
 * — the CSV of every matching row, and, over a table that decides by set, what the decision takes.
 */
export function RankedCountLine<T>({
  shown, filtered, total, rankLabelKey, bulk = false, matching, exportRequest, columnGuide, grain, download,
}: RankedCountLineProps<T>): React.JSX.Element {
  const { t } = useTranslation();
  const rank = rankLabelKey === null ? '' : t(rankLabelKey);
  const extra = grain?.values === undefined ? {} : grain.values(filtered);
  const matched = matching ?? filtered.length;
  const count = matched === total
    ? t(grain?.countKey ?? 'table.ranked.count', { ...extra, shown, filtered: matched, rank })
    : t(grain?.countFilteredKey ?? 'table.ranked.countFiltered', { ...extra, shown, filtered: matched, total, rank });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-caption text-ink-secondary inline-flex items-start gap-1">
          <span role="status" data-ranked-count="">{count}</span>
          {grain !== undefined && (
            <span data-ranked-grain="" className="inline-flex">
              <AboutPopover
                aboutKeys={[grain.descriptionKey]}
                title={t('table.ranked.grainTitle')}
                size="sm"
                {...(grain.descriptionValues === undefined ? {} : { values: grain.descriptionValues })}
              />
            </span>
          )}
        </p>
        {exportRequest !== undefined && (
          <RowCapNotice
            shown={shown}
            total={matched}
            countStated
            always
            exportRequest={() => exportRequest(filtered)}
            {...(download === undefined ? {} : { labels: { keys: download.keys, values: download.values(filtered) } })}
            {...(columnGuide === undefined ? {} : { columnGuide })}
          />
        )}
      </div>
      {bulk && matched > shown && (
        <p data-ranked-bulk="" className="text-caption text-ink-primary max-w-prose">
          {t('table.ranked.bulk', { filtered: matched, shown })}
        </p>
      )}
    </div>
  );
}
